import { Alert, Avatar, Button, Empty, List, Modal, Segmented, Skeleton, Typography } from "antd"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { TextInput } from "../../../components/Input/TextInput.tsx"
import { SearchBar } from "../../../components/Input/SearchBar.tsx"
import { getDirectory } from "../api/services.ts"
import type { Participant } from "../types/messaging.types.ts"

type Mode = "direct" | "group"

interface NewConversationModalProps {
  open: boolean
  projectId: string
  projectName: string
  canCreateGroup: boolean
  onClose: () => void
  onCreateDirect: (person: Participant) => void
  onCreateGroup: (name: string, memberIds: string[]) => void
}

/**
 * New conversation, inside a project.
 *
 * There is no project selector: the tab you opened it from *is* the context,
 * which is the whole point of hanging chat off the project.
 */
export function NewConversationModal({
  open,
  projectId,
  projectName,
  canCreateGroup,
  onClose,
  onCreateDirect,
  onCreateGroup
}: NewConversationModalProps) {
  const { t } = useTranslation()
  const [mode, setMode] = useState<Mode>("direct")
  const [term, setTerm] = useState("")
  const [people, setPeople] = useState<Participant[]>([])
  const [loading, setLoading] = useState(false)
  const [groupName, setGroupName] = useState("")
  const [selected, setSelected] = useState<string[]>([])

  useEffect(() => {
    if (!open) return
    setMode("direct")
    setGroupName("")
    setSelected([])
    setTerm("")
  }, [open])

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    const run = async () => {
      const response = await getDirectory(term.trim(), projectId)
      if (cancelled) return
      setLoading(false)
      if (response.ok && response.data) setPeople(response.data)
    }
    run()
    return () => {
      cancelled = true
    }
  }, [open, term, projectId])

  const toggle = (id: string) =>
    setSelected((current) =>
      current.includes(id) ? current.filter((x) => x !== id) : [...current, id]
    )

  return (
    <Modal
      open={open}
      title={t("messages.newConversation", "New conversation")}
      footer={null}
      onCancel={onClose}
    >
      {/* The destination, stated rather than chosen. */}
      <Alert
        type={"info"}
        className={"mb-3"}
        data-testid={"destination-summary"}
        message={
          <span className={"text-sm"}>
            {t("messages.inProject", "In project")}: <strong>{projectName}</strong>
          </span>
        }
      />

      {canCreateGroup && (
        <Segmented
          block
          className={"mb-3"}
          value={mode}
          onChange={(value) => setMode(value as Mode)}
          options={[
            { value: "direct", label: t("messages.kinds.direct", "Private") },
            { value: "group", label: t("messages.kinds.group", "Group") }
          ]}
        />
      )}

      {mode === "group" && (
        <TextInput
          className={"mb-2"}
          value={groupName}
          onChange={(event) => setGroupName(event.target.value)}
          placeholder={t("messages.groupNamePlaceholder", "Group name, e.g. Electrical")}
          aria-label={t("messages.groupName", "Group name")}
        />
      )}

      <SearchBar
        onSearch={(value) => setTerm(value ?? "")}
        placeholder={t("messages.directory.search", "Search people…")}
        aria-label={t("messages.directory.search", "Search people")}
      />

      <div className={"mt-2 max-h-72 overflow-y-auto"}>
        {loading && <Skeleton active paragraph={{ rows: 3 }} />}
        {!loading && people.length === 0 && (
          <Empty
            description={t(
              "messages.directory.emptyOnProject",
              "Nobody else is assigned to this project yet."
            )}
          />
        )}
        {!loading && people.length > 0 && (
          <List
            dataSource={people}
            renderItem={(person) => (
              <List.Item
                key={person.id}
                data-testid={"directory-row"}
                className={`cursor-pointer hover:bg-slate-50 ${
                  selected.includes(person.id) ? "bg-primary/10" : ""
                }`}
                onClick={() =>
                  mode === "direct" ? onCreateDirect(person) : toggle(person.id)
                }
              >
                <List.Item.Meta
                  avatar={
                    <Avatar src={person.avatar || undefined}>
                      {person.full_name?.[0]}
                    </Avatar>
                  }
                  title={person.full_name}
                  description={person.role_label}
                />
              </List.Item>
            )}
          />
        )}
      </div>

      {mode === "group" && (
        <Button
          block
          type={"primary"}
          className={"mt-3"}
          disabled={!groupName.trim() || selected.length === 0}
          onClick={() => onCreateGroup(groupName.trim(), selected)}
        >
          {t("messages.createGroup", "Create group ({{count}})", {
            count: selected.length
          })}
        </Button>
      )}
    </Modal>
  )
}
